import { Link } from "react-router-dom";

interface SectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

export function Section({ id, title, children }: SectionProps) {
  return (
    <section aria-labelledby={id} className="mt-10">
      <h2
        id={id}
        className="text-xl font-semibold text-[var(--color-text-primary)]"
      >
        {title}
      </h2>
      <div className="mt-4 space-y-4 text-sm leading-relaxed text-[var(--color-text-secondary)]">
        {children}
      </div>
    </section>
  );
}

export function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <span className="mt-0.5 h-1.5 w-1.5 shrink-0 translate-y-1.5 rounded-full bg-[var(--color-primary)]" />
      <span>{children}</span>
    </li>
  );
}

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  crossLink: { to: string; label: string };
  contactEmail: string;
  children: React.ReactNode;
}

export function LegalPageLayout({
  title,
  lastUpdated,
  crossLink,
  contactEmail,
  children,
}: LegalPageLayoutProps) {
  return (
    <div className="min-h-screen bg-[var(--color-surface-secondary)]">
      {/* Top bar */}
      <header className="border-b border-[var(--color-border)] bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--color-primary)] text-white">
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z"
                />
              </svg>
            </div>
            <span className="text-lg font-bold text-[var(--color-text-primary)]">
              ChargeSuite
            </span>
          </Link>
          <Link
            to={crossLink.to}
            className="text-sm text-[var(--color-primary)] hover:underline"
          >
            {crossLink.label}
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-4xl px-6 py-12">
        <div className="rounded-xl border border-[var(--color-border)] bg-white p-8 shadow-sm md:p-12">
          <h1 className="text-3xl font-bold text-[var(--color-text-primary)]">
            {title}
          </h1>
          <p className="mt-2 text-sm text-[var(--color-text-muted)]">
            Last updated: {lastUpdated}
          </p>

          {children}

          {/* Footer */}
          <div className="mt-12 border-t border-[var(--color-border)] pt-6 text-center text-xs text-[var(--color-text-muted)]">
            <p>
              ChargeSuite GmbH &middot; Frankfurt am Main, Germany &middot;{" "}
              <a
                href={`mailto:${contactEmail}`}
                className="hover:underline"
              >
                {contactEmail}
              </a>
            </p>
            <p className="mt-2">
              <Link to={crossLink.to} className="hover:underline">
                {crossLink.label}
              </Link>{" "}
              &middot;{" "}
              <Link to="/" className="hover:underline">
                Back to Dashboard
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
